import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

type SingleResponse<T> = Promise<{ data: T | null; error: Error | null }>;
type MaybeSingleBuilder<T> = {
    eq(column: "user_id", value: string): {
        maybeSingle(): SingleResponse<T>;
        select(): { single(): SingleResponse<T> };
    };
};
type PrintSettingsTable = {
    select(columns: string): MaybeSingleBuilder<PrintSettings>;
    update(values: Record<string, unknown>): {
        eq(column: "user_id", value: string): { select(): { single(): SingleResponse<PrintSettings> } };
    };
    insert(values: Record<string, unknown>): { select(): { single(): SingleResponse<PrintSettings> } };
};

const printSettingsTable = () =>
    (supabase as unknown as { from(table: "print_settings"): PrintSettingsTable }).from("print_settings");

export type PrintHeaderLayout = "inline" | "inline_apart" | "left" | "right" | "center" | "full";
export type PrintPaperSize = "A4" | "A5" | "Letter";

export interface PrintSettings {
    id: string;
    user_id: string;
    clinic_id?: string | null;
    background_image_url: string | null;
    term_background_image_url: string | null;
    header_text: string | null;
    term_header_text: string | null;
    footer_text: string | null;
    term_footer_text: string | null;
    header_image_url: string | null;
    term_header_image_url: string | null;
    footer_image_url: string | null;
    term_footer_image_url: string | null;
    header_layout: PrintHeaderLayout | null;
    term_header_layout: PrintHeaderLayout | null;
    footer_layout: 'left' | 'right' | 'center' | null;
    term_footer_layout: 'left' | 'right' | 'center' | null;
    text_font_family: string | null;
    term_text_font_family: string | null;
    text_color: string | null;
    term_text_color: string | null;
    title_color: string | null;
    term_title_color: string | null;
    header_text_color: string | null;
    term_header_text_color: string | null;
    footer_text_color: string | null;
    term_footer_text_color: string | null;
    font_size_pt: number | null;
    term_font_size_pt: number | null;
    line_height: number | null;
    term_line_height: number | null;
    header_logo_height_px: number | null;
    term_header_logo_height_px: number | null;
    show_issuer_block: boolean;
    term_show_issuer_block: boolean;
    stamp_show_crmv: boolean;
    term_stamp_show_crmv: boolean;
    stamp_show_mapa: boolean;
    term_stamp_show_mapa: boolean;
    paper_size: PrintPaperSize;
    term_paper_size: PrintPaperSize;
    margin_top_mm: number | null;
    term_margin_top_mm: number | null;
    margin_side_mm: number | null;
    term_margin_side_mm: number | null;
    margin_bottom_mm: number | null;
    term_margin_bottom_mm: number | null;
    created_at: string;
    updated_at: string;
}

export interface PrintSettingsInput {
    background_image_url?: string | null;
    term_background_image_url?: string | null;
    header_text?: string | null;
    term_header_text?: string | null;
    footer_text?: string | null;
    term_footer_text?: string | null;
    header_image_url?: string | null;
    term_header_image_url?: string | null;
    footer_image_url?: string | null;
    term_footer_image_url?: string | null;
    header_layout?: PrintHeaderLayout | null;
    term_header_layout?: PrintHeaderLayout | null;
    footer_layout?: 'left' | 'right' | 'center' | null;
    term_footer_layout?: 'left' | 'right' | 'center' | null;
    text_font_family?: string | null;
    term_text_font_family?: string | null;
    text_color?: string | null;
    term_text_color?: string | null;
    title_color?: string | null;
    term_title_color?: string | null;
    header_text_color?: string | null;
    term_header_text_color?: string | null;
    footer_text_color?: string | null;
    term_footer_text_color?: string | null;
    font_size_pt?: number | null;
    term_font_size_pt?: number | null;
    line_height?: number | null;
    term_line_height?: number | null;
    header_logo_height_px?: number | null;
    term_header_logo_height_px?: number | null;
    show_issuer_block?: boolean;
    term_show_issuer_block?: boolean;
    stamp_show_crmv?: boolean;
    term_stamp_show_crmv?: boolean;
    stamp_show_mapa?: boolean;
    term_stamp_show_mapa?: boolean;
    paper_size?: PrintPaperSize;
    term_paper_size?: PrintPaperSize;
    margin_top_mm?: number | null;
    term_margin_top_mm?: number | null;
    margin_side_mm?: number | null;
    term_margin_side_mm?: number | null;
    margin_bottom_mm?: number | null;
    term_margin_bottom_mm?: number | null;
}

type UpdatePrintSettingsPayload =
    | PrintSettingsInput
    | {
        settings: PrintSettingsInput;
        suppressSuccessToast?: boolean;
        suppressErrorToast?: boolean;
    };

export const usePrintSettings = () => {
    const { t } = useTranslation("settings");
    return useQuery({
        queryKey: ["print-settings"],
        queryFn: async () => {
            const { data: userData } = await supabase.auth.getUser();
            if (!userData.user) throw new Error(t("userNotAuthenticated"));

            const { data, error } = await printSettingsTable()
                .select("*")
                .eq("user_id", userData.user.id)
                .maybeSingle();

            if (error) throw error;
            return (data ?? null) as PrintSettings | null;
        },
    });
};

export const useUpdatePrintSettings = () => {
    const { t } = useTranslation("settings");
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: UpdatePrintSettingsPayload) => {
            const settings = "settings" in payload ? payload.settings : payload;
            const { data: userData } = await supabase.auth.getUser();
            if (!userData.user) throw new Error(t("userNotAuthenticated"));

            const { data: existing } = await printSettingsTable()
                .select("id")
                .eq("user_id", userData.user.id)
                .maybeSingle();

            if (existing) {
                // Update existing
                const { data, error } = await printSettingsTable()
                    .update({
                        ...settings,
                        updated_at: new Date().toISOString(),
                    })
                    .eq("user_id", userData.user.id)
                    .select()
                    .single();

                if (error) throw error;
                return data as PrintSettings;
            } else {
                // Insert new
                const { data, error } = await printSettingsTable()
                    .insert({
                        user_id: userData.user.id,
                        ...settings,
                    })
                    .select()
                    .single();

                if (error) throw error;
                return data as PrintSettings;
            }
        },
        onSuccess: async (_, variables) => {
            await queryClient.invalidateQueries({ queryKey: ["print-settings"], refetchType: "all" });
            if (!("settings" in variables) || !variables.suppressSuccessToast) {
                toast.success(t("settingsSaved"));
            }
        },
        onError: (error, variables) => {
            console.error("Erro ao salvar configurações:", error);
            if (!("settings" in variables) || !variables.suppressErrorToast) {
                toast.error(t("saveError"));
            }
        },
    });
};
